import { type ReactNode } from 'react'

import {
  BulletedList,
  IconCheck,
  Modal,
  Space,
  SubText,
  TertiaryButton,
  Text,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { FeaturePromotionModalImage } from './styles/FeaturePromotionModalStyled'

export interface IFeaturePromotionModalProps {
  open: boolean
  onClose: () => void
  header: ReactNode
  imageSrc: string
  imageAlt: string
  bullets: ReactNode[]
  cta: ReactNode
  descriptionText?: ReactNode
  disclaimerText?: ReactNode
  learnMoreUrl?: string
}

export const FeaturePromotionModal = ({
  open,
  onClose,
  header,
  imageSrc,
  imageAlt,
  bullets,
  cta,
  descriptionText,
  disclaimerText,
  learnMoreUrl,
}: IFeaturePromotionModalProps) => {
  const intl = useProcurifyIntl()

  return (
    <Modal
      aria-labelledby='FeaturePromotionModal-title'
      maxWidth='sm'
      open={open}
      onClose={onClose}
    >
      <Modal.Card>
        <Modal.Header id='FeaturePromotionModal-title' onClose={onClose}>
          {header}
        </Modal.Header>
        <Modal.Content>
          <Space size='large'>
            {descriptionText && (
              <Text color='textSecondary'>{descriptionText}</Text>
            )}
            <FeaturePromotionModalImage src={imageSrc} alt={imageAlt} />
            <BulletedList>
              {bullets.map((bullet, index) => (
                <BulletedList.Item
                  key={index}
                  iconComponent={<IconCheck color='info' />}
                >
                  {bullet}
                </BulletedList.Item>
              ))}
            </BulletedList>
            <Space.Divider />
            <Space
              direction='horizontal'
              justify='space-between'
              align='center'
            >
              <SubText color='textSecondary'>{disclaimerText}</SubText>
              <Space direction='horizontal'>
                {learnMoreUrl && (
                  <a href={learnMoreUrl} target='_blank' rel='noreferrer'>
                    <TertiaryButton>
                      {intl.formatMessage(messages.learnMoreButtonText)}
                    </TertiaryButton>
                  </a>
                )}
                {cta}
              </Space>
            </Space>
          </Space>
        </Modal.Content>
      </Modal.Card>
    </Modal>
  )
}
FeaturePromotionModal.displayName = 'FeaturePromotionModal'
